/**
 * Adjusted from https://github.com/deoxxa/paginator/blob/master/index.js
 */
export class PaginatorInfo {
  constructor(
    private numResultsPerPage: number,
    private length: number,
  ) {
    this.numResultsPerPage = numResultsPerPage || 25
    this.length = length || 10
  }

  build(totalResults: number, currentPage: number = 1) {
    // We want the number of pages, rounded-sm up to the nearest page.
    const numPages = Math.ceil(totalResults / this.numResultsPerPage)

    // Obviously we can't be on a negative or 0 page.
    if (currentPage < 1) {
      currentPage = 1
    }
    // If the user has done something like /page/99999 we want to clamp that
    // back down.
    if (currentPage > numPages) {
      currentPage = numPages
    }

    // This is the first page to be displayed as a numbered link.
    let firstPage = Math.max(1, currentPage - Math.floor(this.length / 2))

    // And here's the last page to be displayed specifically.
    let lastPage = Math.min(numPages, currentPage + Math.floor(this.length / 2))

    // This is triggered if we're at or near one of the extremes; we won't have
    // enough page links. We need to adjust our bounds accordingly.
    if (lastPage - firstPage + 1 < this.length) {
      if (currentPage < numPages / 2) {
        lastPage = Math.min(
          numPages,
          lastPage + (this.length - (lastPage - firstPage)),
        )
      } else {
        firstPage = Math.max(
          1,
          firstPage - (this.length - (lastPage - firstPage)),
        )
      }
    }

    // This can be triggered if the user wants an odd number of pages.
    if (lastPage - firstPage + 1 > this.length) {
      // We want to move towards whatever extreme we're closest to at the time.
      if (currentPage > numPages / 2) {
        firstPage++
      } else {
        lastPage--
      }
    }

    // First result on the page. This, along with the field below, can be used
    // to do "showing x to y of z results" style things.
    let firstResult = this.numResultsPerPage * (currentPage - 1)

    if (firstResult < 0) {
      firstResult = 0
    }

    // Last result on the page.
    let lastResult = this.numResultsPerPage * currentPage - 1

    if (lastResult < 0) {
      lastResult = 0
    }

    if (lastResult > Math.max(totalResults - 1, 0)) {
      lastResult = Math.max(totalResults - 1, 0)
    }

    // GIMME THAT OBJECT
    return {
      totalPages: numPages,
      pages: Math.min(lastPage - firstPage + 1, numPages),
      currentPage: currentPage,
      firstPage: firstPage,
      lastPage: lastPage,
      previousPage: currentPage - 1,
      nextPage: currentPage + 1,
      hasPreviousPage: currentPage > 1,
      hasNextPage: currentPage < numPages,
      totalResults: totalResults,
      results: Math.min(lastResult - firstResult + 1, totalResults),
      firstResult: firstResult,
      lastResult: lastResult,
    }
  }
}
